'use client'

import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getHivePosts } from '../actions/post.actions'
import type { Post } from '../post'

/**
 * Hive feed reader. Wraps `GET /api/post/hive/:handle/posts`, which
 * lives under the post namespace on the Go server even though it's keyed
 * by hive id/handle — so the wire (and this hook) sits with the rest of
 * the post feature rather than under hives.
 */
export const hiveFeedKeys = {
  /** Root prefix — matches every hive feed for broad invalidation. */
  all: ['hive-feed'] as const,
  byHive: (hiveId: string) => [...hiveFeedKeys.all, hiveId] as const,
}

export const hiveFeedQueryKey = hiveFeedKeys.byHive
const DEFAULT_PAGE_SIZE = 10

function flattenUniquePostPages(pages: Post[][]): Post[] {
  const seen = new Set<string>()
  const out: Post[] = []
  for (const page of pages) {
    for (const post of page) {
      if (seen.has(post.id)) continue
      seen.add(post.id)
      out.push(post)
    }
  }
  return out
}

export function useHiveFeed(hiveId: string, options?: { initialData?: Post[] }) {
  const limit = DEFAULT_PAGE_SIZE
  const query = useInfiniteQuery({
    queryKey: hiveFeedQueryKey(hiveId),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => getHivePosts(hiveId, { skip: pageParam, limit }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < limit ? undefined : allPages.length * limit,
    initialData: options?.initialData
      ? { pages: [options.initialData], pageParams: [0] }
      : undefined,
    enabled: !!hiveId,
  })

  const data = useMemo(
    () => (query.data ? flattenUniquePostPages(query.data.pages) : undefined),
    [query.data]
  )

  return { ...query, data }
}
